import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { CreateObjectButton } from '@/components/CreateObjectButton'
import { Layers } from 'lucide-react'
import { toast } from 'sonner'

import { CardContent, CardIcon } from '@/components/ui/card'
import { SiteGroupsApi, type SiteGroup } from '@/services/sites/siteGroupsApi'
import { apiErrorDescription } from '@/services/core/apiError'
import type { ListSort } from '@/services/core/paging'
import { usePersistedState } from '@/hooks/usePersistedState'
import { AppShell } from '../../components/AppShell'
import { BulkActionBar } from '../../components/BulkActionBar'
import { ListCard } from '../../components/ListCard'
import { ListCardHeader } from '../../components/ListCardHeader'
import { SearchInput } from '../../components/SearchInput'
import { SiteGroupTable } from '../../components/SiteGroupTable'
import { StatusFilterButton } from '../../components/StatusFilter'
import { useUrlSearch } from '@/hooks/useUrlSearch'
import { useNetwork } from '@/contexts/NetworkContext'
import { ListPagination } from '../../components/ListPagination'
import { PageSizeSelect } from '../../components/PageSizeSelect'

export function SiteGroupsPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [search, setSearch] = useUrlSearch()
  const { scopeKey, scopeReady } = useNetwork()
  const [statusFilter, setStatusFilter] = usePersistedState<'all' | 'ACTIVE' | 'INACTIVE'>(
    'filters.siteGroups.status',
    'ACTIVE'
  )
  const [page, setPage] = useState(1)
  const [sort, setSort] = useState<ListSort | null>(null)
  const [pageSize, setPageSize] = useState(25)
  const [selection, setSelection] = useState<SiteGroup[]>([])
  const [bulkLoading, setBulkLoading] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['site-groups', scopeKey, search, statusFilter, page, pageSize, sort],
    queryFn: () => SiteGroupsApi.getPage({ page, pageSize, search, status: statusFilter, sort }),
    enabled: scopeReady,
    staleTime: 5 * 60 * 1000,
  })
  const siteGroups = data?.results ?? []
  const totalCount = data?.totalCount ?? 0

  /** Back to the first page whenever the rows underneath change. */
  const refilter = (apply: () => void) => {
    apply()
    setPage(1)
    setSelection([])
  }

  const bulkSetStatus = async (state: 'ACTIVE' | 'INACTIVE') => {
    setBulkLoading(true)
    try {
      await Promise.all(
        selection.map((g) => SiteGroupsApi.update(g.id, { name: g.name, objectState: state }))
      )
      queryClient.invalidateQueries({ queryKey: ['site-groups'] })
      toast.success(t('common.done'), {
        description: t(
          state === 'ACTIVE'
            ? 'siteGroups.doneDetail_activate'
            : 'siteGroups.doneDetail_deactivate',
          { count: selection.length }
        ),
        duration: 3000,
      })
      setSelection([])
    } catch (e) {
      toast.error(t('common.error'), { description: apiErrorDescription(e), duration: 5000 })
    } finally {
      setBulkLoading(false)
    }
  }

  const handleSort = (next: ListSort | null) => {
    setSort(next)
    setPage(1)
  }

  return (
    <AppShell crumbs={[{ label: t('siteGroups.title') }]}>
      <div className="ad-page">
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <h1>{t('siteGroups.title')}</h1>
            <p>{t('siteGroups.subtitle')}</p>
          </div>
          <div className="ad-page__actions">
            <CreateObjectButton type="siteGroup" size="default" />
          </div>
        </div>

        <ListCard tone="delivery">
          <ListCardHeader
            title={
              <>
                <CardIcon icon={Layers} />
                {t('siteGroups.title')}
              </>
            }
            count={totalCount}
            actions={
              <PageSizeSelect
                value={pageSize}
                onChange={(size) => refilter(() => setPageSize(size))}
              />
            }
            controls={
              <>
                <SearchInput value={search} onChange={(v) => refilter(() => setSearch(v))} />
                <StatusFilterButton
                  value={statusFilter}
                  onChange={(v) => refilter(() => setStatusFilter(v))}
                />
              </>
            }
          />

          <BulkActionBar
            count={selection.length}
            actions={[
              {
                label: t('common.activate'),
                icon: 'pi pi-check-circle',
                severity: 'success',
                onClick: () => bulkSetStatus('ACTIVE'),
              },
              {
                label: t('common.deactivate'),
                icon: 'pi pi-ban',
                severity: 'danger',
                onClick: () => bulkSetStatus('INACTIVE'),
              },
            ]}
            onClear={() => setSelection([])}
            loading={bulkLoading}
          />

          <CardContent className="p-0">
            <SiteGroupTable
              siteGroups={siteGroups}
              loading={isLoading}
              selection={selection}
              onSelectionChange={setSelection}
              emptyMessage={t('siteGroups.noSiteGroupsFound')}
              sort={sort}
              onSortChange={handleSort}
            />
          </CardContent>

          <ListPagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
            summary={t('siteGroups.siteGroupCount', { count: totalCount })}
          />
        </ListCard>
      </div>
    </AppShell>
  )
}
